/**
 * Modal de création de facture d'acompte
 * Permet de choisir % ou montant fixe, voir le résumé des acomptes existants
 * Design System: contexte Dashboard (bg-white, text-gray-900, teal-600)
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { quotesAPI } from '@/utils/api';
import type { DepositSummary } from '../types/billing.types';

interface DepositInvoiceModalProps {
  quoteId: string;
  quoteNumber: string;
  quoteTotalTTC: number;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    depositType: 'PERCENT' | 'FIXED';
    depositValue: number;
    issueDate: string;
    dueDate: string;
  }) => void;
  isLoading?: boolean;
}

export function DepositInvoiceModal({
  quoteId,
  quoteNumber,
  quoteTotalTTC,
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
}: DepositInvoiceModalProps) {
  const { t } = useTranslation('invoices');
  const [depositType, setDepositType] = useState<'PERCENT' | 'FIXED'>('PERCENT');
  const [depositValue, setDepositValue] = useState<number>(30);
  const [issueDate, setIssueDate] = useState(new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState(
    new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );

  // Fetch deposit summary
  const { data: summary } = useQuery<DepositSummary>({
    queryKey: ['deposit-summary', quoteId],
    queryFn: () => quotesAPI.getDepositSummary(quoteId),
    enabled: isOpen,
  });

  // Preview du montant calculé
  const previewAmount = depositType === 'PERCENT'
    ? (quoteTotalTTC * depositValue) / 100
    : depositValue;

  const remainingAfter = (summary?.remainingTTC ?? quoteTotalTTC) - previewAmount;
  const isOverLimit = remainingAfter < -0.01;

  useEffect(() => {
    if (!isOpen) {
      setDepositType('PERCENT');
      setDepositValue(30);
    }
  }, [isOpen]);

  // Fermeture sur touche Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isLoading, onClose]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isOverLimit || depositValue <= 0) return;
    onSubmit({ depositType, depositValue, issueDate, dueDate });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        // Fermeture sur clic overlay (uniquement si clic direct sur le fond, pas sur le contenu)
        if (e.target === e.currentTarget && !isLoading) onClose();
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('deposit.createTitle', "Facture d'acompte")}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {t('deposit.forQuote', 'Sur devis {{number}}', { number: quoteNumber })}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 space-y-4">
            {/* Résumé acomptes existants */}
            {summary && summary.deposits.length > 0 && (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
                <h3 className="text-sm font-medium text-teal-800 mb-2">
                  {t('deposit.existingDeposits', 'Acomptes existants')}
                </h3>
                {summary.deposits.map((d) => (
                  <div key={d.id} className="flex justify-between text-sm text-teal-700">
                    <span>{d.number} — {d.depositPercent ? `${d.depositPercent}%` : ''}</span>
                    <span className="font-medium">{d.totalTTC.toFixed(2)} €</span>
                  </div>
                ))}
                <div className="mt-2 pt-2 border-t border-teal-300 flex justify-between text-sm font-semibold text-teal-900">
                  <span>{t('deposit.remaining', 'Solde restant')}</span>
                  <span>{summary.remainingTTC.toFixed(2)} €</span>
                </div>
              </div>
            )}

            {/* Toggle % / Montant fixe */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('deposit.type', "Type d'acompte")}
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${
                    depositType === 'PERCENT'
                      ? 'bg-teal-600 text-white border-teal-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                  onClick={() => setDepositType('PERCENT')}
                >
                  {t('deposit.percent', 'Pourcentage')}
                </button>
                <button
                  type="button"
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${
                    depositType === 'FIXED'
                      ? 'bg-teal-600 text-white border-teal-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                  onClick={() => setDepositType('FIXED')}
                >
                  {t('deposit.fixed', 'Montant fixe')}
                </button>
              </div>
            </div>

            {/* Input montant */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {depositType === 'PERCENT'
                  ? t('deposit.percentValue', 'Pourcentage (%)')
                  : t('deposit.fixedValue', 'Montant TTC (€)')}
              </label>
              <input
                type="number"
                step={depositType === 'PERCENT' ? '1' : '0.01'}
                min="0"
                max={depositType === 'PERCENT' ? '100' : undefined}
                value={depositValue}
                onChange={(e) => setDepositValue(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
              />
            </div>

            {/* Preview calcul */}
            <div className="bg-gray-50 rounded-lg p-3 space-y-1">
              <div className="flex justify-between text-sm text-gray-600">
                <span>{t('deposit.baseAmount', 'Base TTC du devis')}</span>
                <span>{quoteTotalTTC.toFixed(2)} €</span>
              </div>
              <div className="flex justify-between text-sm font-semibold text-gray-900">
                <span>{t('deposit.depositAmount', "Montant de l'acompte")}</span>
                <span className={isOverLimit ? 'text-red-600' : 'text-teal-600'}>
                  {previewAmount.toFixed(2)} €
                </span>
              </div>
              {isOverLimit && (
                <p className="text-xs text-red-600 mt-1">
                  {t('deposit.overLimit', "L'acompte dépasse le solde restant")}
                </p>
              )}
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('deposit.issueDate', "Date d'émission")}
                </label>
                <input
                  type="date"
                  value={issueDate}
                  onChange={(e) => setIssueDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('deposit.dueDate', "Date d'échéance")}
                </label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
                  required
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={isLoading}
            >
              {t('common:cancel', 'Annuler')}
            </button>
            <button
              type="submit"
              disabled={isLoading || isOverLimit || depositValue <= 0}
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading
                ? t('deposit.creating', 'Création...')
                : t('deposit.create', "Créer l'acompte")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
